import { useEffect, useState, useCallback, useRef } from "react";
import { fetchUnreadCount } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { logger } from "@/lib/logger";

// Polls /api/me/unread-count every 20s when tab is visible.
// Slows to 60s when hidden. Exposes manual refresh().
export function useUnreadCount() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setCount(0);
      return;
    }
    try {
      const r = await fetchUnreadCount();
      setCount(r.count || 0);
    } catch (e) {
      logger.warn("Failed to fetch unread count:", e?.message || e);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setCount(0);
      return;
    }
    refresh();
    const tick = () => {
      const delay = document.visibilityState === "visible" ? 20000 : 60000;
      timerRef.current = setTimeout(async () => {
        await refresh();
        tick();
      }, delay);
    };
    tick();
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, refresh]);

  return { count, refresh };
}
